import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { removeBackground, Config } from '@imgly/background-removal';
import { setLocalLogoByLocalId } from '../../../../../../../../crud/management_api/local_configurations';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import { toast } from 'react-toastify';
import { X } from 'react-bootstrap-icons';


interface PhotoEditorProps {
  image: File;
  setFinalImage: (image: File) => void;
  show: boolean;
  handleClose: () => void;
  handleSave: () => void;
  onLogoUpdated?: (newLogoUrl: string) => void;
}

const PhotoEditor: React.FC<PhotoEditorProps> = ({
  image,
  setFinalImage,
  show,
  handleClose,
  handleSave,
  onLogoUpdated,
}) => {
  const [originalImageSrc, setOriginalImageSrc] = useState<string | null>(null);
  const [processedImageSrc, setProcessedImageSrc] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);

  // Load the original image as a data URL when the image changes
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setOriginalImageSrc(e.target?.result as string);
      // Reset any previously processed image
      setProcessedImageSrc(null);
    };
    reader.readAsDataURL(image);
  }, [image]);

  // Remove background using IMG.LY's background removal (runs entirely in the browser)
  const handleBackgroundRemoval = async () => {
    setProcessing(true);
    try {
      const config: Config = {
        // Optional configuration:
        // debug: true,
        // output: { format: 'image/png', quality: 0.8, type: 'foreground' },
      };
      // Pass the image file directly to removeBackground.
      const blob: Blob = await removeBackground(image, config);
      const processedUrl = URL.createObjectURL(blob);
      setProcessedImageSrc(processedUrl);
    } catch (error) {
      console.error('Error removing background:', error);
      toast.error('Error al eliminar el fondo');
    } finally {
      setProcessing(false);
    }
  };

  // Revert to using the original image
  const handleRevert = () => {
    setProcessedImageSrc(null);
  };

  // Helper: convert a data URL (which includes the base64 data) into just the base64 string.
  const dataURLtoBase64 = (dataUrl: string): string => {
    return dataUrl.split(',')[1];
  };

  // Handle final save by calling your API. Show a mini spinner while uploading.
  const handleFinalSave = async () => {
    setUploading(true);
    try {
      let finalDataUrl = originalImageSrc;
      // Si hay imagen procesada, convertir el blob a DataURL
      if (processedImageSrc) {
        // processedImageSrc es un objeto URL, necesitamos obtener el blob y convertirlo a DataURL
        const response = await fetch(processedImageSrc);
        const blob = await response.blob();
        const reader = new FileReader();
        finalDataUrl = await new Promise<string>((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      if (!finalDataUrl) {
        toast.error('No hay imagen para guardar');
        setUploading(false);
        return;
      }
      const base64Data = dataURLtoBase64(finalDataUrl);
      const userContext = localStorage.getItem('user');
      const localId = userContext ? JSON.parse(userContext).local_id : 0;
      await setLocalLogoByLocalId(localId, base64Data);
      toast.success('Logo actualizado exitosamente');
      if (onLogoUpdated) {
        onLogoUpdated(finalDataUrl);
      }
      handleSave();
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Error al subir el logo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header>
        <div className='form-modal-title-container'>
        <Modal.Title className='form-modal-title'>Editar foto</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleClose}>
                  <X size={24} />
                </Button>
      </Modal.Header>
      <Modal.Body>
        {uploading ? (
          <div style={{ textAlign: 'center' }}>
            <MiniLoadingSpinner />
            <p>Subiendo logo...</p>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center' }}>
              {processing && <p>Procesando imagen...</p>}
              {!processing && (
                <img
                  src={processedImageSrc ? processedImageSrc : originalImageSrc || ''}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: '500px' }}
                />
              )}
            </div>
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <Button
                variant="none"
                className='mp-button'
                onClick={handleBackgroundRemoval}
                disabled={processing || uploading}
              >
                Eliminar Fondo
              </Button>
              {processedImageSrc && (
                <Button
                  variant="none"
                  className='mp-button'
                  onClick={handleRevert}
                  style={{ marginLeft: '0.5rem' }}
                  disabled={processing || uploading}
                >
                  Revertir
                </Button>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className='form-modal-footer-custom'>
        <Button variant="none" className='form-button-cancel' onClick={handleClose} disabled={uploading}>
          Cancelar
        </Button>
        <Button variant="none" className='form-button-create' onClick={handleFinalSave} disabled={uploading || processing}>
          Guardar
        </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PhotoEditor;
